import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import toast from "react-hot-toast";
import { Search } from "lucide-react";
import { Button, Card, Badge } from "../components/legacy-ui.js";
import { AppPageHeader, AppTable } from "../components/app-ui.js";
import { companyPageTitle } from "../utils/company.js";
import { formatDate } from "../utils/formatters.js";
import { getInvoiceActivity } from "../utils/api.js";
import { LoadingState } from "../components/motion.js";

const ACTION_OPTIONS = [
  { value: "", label: "Todas las acciones" },
  { value: "invoice_create", label: "Creó factura" },
  { value: "invoice_update", label: "Editó factura" },
  { value: "invoice_delete", label: "Eliminó factura" },
  { value: "payment_create", label: "Registró pago" },
  { value: "payment_update", label: "Editó pago" },
  { value: "payment_delete", label: "Eliminó pago" },
];

const ACTION_META = {
  invoice_create: { label: "Creó factura", type: "success" },
  invoice_update: { label: "Editó factura", type: "info" },
  invoice_delete: { label: "Eliminó factura", type: "danger" },
  payment_create: { label: "Registró pago", type: "success" },
  payment_update: { label: "Editó pago", type: "warning" },
  payment_delete: { label: "Eliminó pago", type: "danger" },
};

const actorName = (row) =>
  row.user?.fullName || row.userName || row.user?.username || row.userUsername || "Sistema";

const InvoiceActivityModule = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [action, setAction] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, action]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getInvoiceActivity({
        search: debouncedSearch || undefined,
        action: action || undefined,
        page,
        limit: 40,
      });
      setRows(res.data.data || []);
      setTotalPages(res.data.totalPages || 1);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error(error.response?.data?.error || "No se pudo cargar el registro", {
        position: "top-center",
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, action, page]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  return (
    <>
      <Helmet>
        <title>{companyPageTitle("Registro de facturas")}</title>
      </Helmet>
      <div className="app-page">
        <div className="app-section-head">
          <AppPageHeader
            title="Registro de facturas"
            subtitle="Quién creó, editó o eliminó facturas y pagos"
            actions={
              <Button variant="secondary" onClick={fetchLogs}>
                Actualizar
              </Button>
            }
          />
          <div className="flex flex-col sm:flex-row gap-2 mt-4 mb-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Buscar factura, cliente o usuario…"
                className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-300 rounded-lg text-sm placeholder-gray-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <select
              className="px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm"
              value={action}
              onChange={(e) => setAction(e.target.value)}
            >
              {ACTION_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-xs text-gray-500 mb-2">{total} movimiento{total === 1 ? "" : "s"}</p>
        </div>

        <div className="app-content">
          {loading ? (
            <LoadingState label="Cargando movimientos" />
          ) : rows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-12">
              Aún no hay movimientos registrados.
            </p>
          ) : (
            <>
              <div className="md:hidden space-y-3">
                {rows.map((row) => {
                  const meta = ACTION_META[row.action] || { label: row.action, type: "info" };
                  return (
                    <Card key={row._id} className="p-4 space-y-2">
                      <div className="flex justify-between gap-2 items-start">
                        <Badge type={meta.type}>{meta.label}</Badge>
                        <span className="text-[11px] text-gray-400">{formatDate(row.createdAt)}</span>
                      </div>
                      <p className="text-sm text-gray-800">{row.summary}</p>
                      <p className="text-xs text-gray-500">
                        {actorName(row)}
                        {row.invoiceNumber ? ` · Factura #${row.invoiceNumber}` : ""}
                        {row.clientName ? ` · ${row.clientName}` : ""}
                        {row.source === "whatsapp" ? " · WhatsApp" : ""}
                      </p>
                    </Card>
                  );
                })}
              </div>

              <div className="hidden md:block">
                <AppTable
                  columns={[
                    { key: "date", title: "Fecha" },
                    { key: "user", title: "Usuario" },
                    { key: "action", title: "Acción" },
                    { key: "invoice", title: "Factura" },
                    { key: "detail", title: "Detalle" },
                  ]}
                >
                  {rows.map((row) => {
                    const meta = ACTION_META[row.action] || { label: row.action, type: "info" };
                    return (
                      <tr key={row._id} className="hover:bg-gray-50">
                        <td className="px-6 py-3.5 text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(row.createdAt)}
                        </td>
                        <td className="px-6 py-3.5 text-sm text-gray-800">
                          {actorName(row)}
                          {row.userUsername ? (
                            <span className="block text-[11px] text-gray-400">@{row.userUsername}</span>
                          ) : null}
                        </td>
                        <td className="px-6 py-3.5">
                          <Badge type={meta.type}>{meta.label}</Badge>
                        </td>
                        <td className="px-6 py-3.5 text-sm text-gray-700">
                          {row.invoiceNumber ? `#${row.invoiceNumber}` : "—"}
                          {row.clientName ? (
                            <span className="block text-xs text-gray-400">{row.clientName}</span>
                          ) : null}
                        </td>
                        <td className="px-6 py-3.5 text-sm text-gray-600">
                          {row.summary}
                          {row.source === "whatsapp" ? (
                            <span className="ml-1 text-[11px] text-emerald-700">WhatsApp</span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </AppTable>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-3 mt-4">
                  <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                    Anterior
                  </Button>
                  <span className="text-sm text-gray-500">
                    {page} / {totalPages}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default InvoiceActivityModule;
